// Copyright © 2026 Alex Bruce. All rights reserved.
// Proprietary and confidential. Unauthorized copying, modification,
// or distribution is strictly prohibited.

import React, { useState, useEffect, useCallback } from "react";
import type { ProjectShare } from "../types";
import type { SupabaseStorageProvider } from "../lib/supabaseStorage";
import type { Theme } from "../lib/theme";
import * as Icon from "./OEIcons";

const FONT = "'Inter','Avenir Next','Century Gothic',sans-serif";

interface ShareDialogProps {
  projectId: string;
  projectName: string;
  ownerId: string;
  ownerName: string;
  currentOrgId: string | null;
  currentOrgName: string | null;
  storage: SupabaseStorageProvider;
  userOrgs: Array<{ id: string; name: string }>;
  theme: Theme;
  isOwner: boolean;
  onClose: () => void;
  onChanged: () => void;
}

export default function ShareDialog({
  projectId, projectName, ownerId, ownerName, currentOrgId, currentOrgName,
  storage, userOrgs, theme: T, isOwner, onClose, onChanged,
}: ShareDialogProps) {
  const [shares, setShares] = useState<ProjectShare[]>([]);
  const [loading, setLoading] = useState(true);
  const [email, setEmail] = useState("");
  const [role, setRole] = useState<string>("editor");
  const [sharing, setSharing] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [orgId, setOrgId] = useState<string>(currentOrgId || "");

  const loadShares = useCallback(async () => {
    setLoading(true);
    const list = await storage.listProjectShares(projectId);
    setShares(list);
    setLoading(false);
  }, [storage, projectId]);

  useEffect(() => { loadShares(); }, [loadShares]);

  const handleShare = async () => {
    const trimmed = email.trim();
    if (!trimmed) return;
    setSharing(true);
    setError("");
    setSuccess("");
    const result = await storage.shareProject(projectId, trimmed, role);
    setSharing(false);
    if (result.error) { setError(result.error); }
    else { setSuccess(`Shared with ${trimmed}`); setEmail(""); loadShares(); onChanged(); }
  };

  const handleUnshare = async (userId: string) => {
    await storage.unshareProject(projectId, userId);
    loadShares();
    onChanged();
  };

  const handleOrgChange = async (newOrgId: string) => {
    setOrgId(newOrgId);
    await storage.updateProjectOrg(projectId, newOrgId || null);
    onChanged();
  };

  const selectStyle: React.CSSProperties = {
    background: T.bgInput, border: `1px solid ${T.borderSecondary}`, borderRadius: 3,
    color: T.textSecondary, padding: "7px 10px", fontSize: 12, fontFamily: FONT, outline: "none",
  };

  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 200, background: T.bgModalBackdrop, display: "flex", alignItems: "center", justifyContent: "center" }}
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={{ background: T.bgModal, border: `1px solid ${T.borderSecondary}`, borderRadius: 6, padding: "28px 32px", width: 440, maxWidth: "90vw", maxHeight: "85vh", overflowY: "auto", fontFamily: FONT }}>

        {/* Header */}
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 20 }}>
          <div>
            <div style={{ fontSize: 14, fontWeight: 700, color: T.textSubLabel }}>Share Project</div>
            <div style={{ fontSize: 11, color: T.textMuted, marginTop: 2 }}>{projectName}</div>
          </div>
          <button onClick={onClose}
            style={{ background: "none", border: "none", color: T.textWeak, cursor: "pointer", padding: "4px 8px" }}>
            <Icon.X size={12}/>
          </button>
        </div>

        {/* Organization assignment (owner only) */}
        {isOwner && (
          <div style={{ marginBottom: 20 }}>
            <div style={{ fontSize: 10, color: T.textWeak, letterSpacing: ".1em", textTransform: "uppercase", marginBottom: 6 }}>Organization</div>
            <select value={orgId} onChange={e => handleOrgChange(e.target.value)} style={{ ...selectStyle, width: "100%", boxSizing: "border-box" }}>
              <option value="">Personal (no organization)</option>
              {userOrgs.map(o => <option key={o.id} value={o.id}>{o.name}</option>)}
            </select>
            <div style={{ fontSize: 10, color: T.textPlaceholder, marginTop: 4 }}>
              {orgId ? "All organization members can view and edit this project." : "Only you and individually shared users can access this project."}
            </div>
          </div>
        )}

        {/* Non-owner: show org info */}
        {!isOwner && currentOrgName && (
          <div style={{ marginBottom: 20, padding: "10px 14px", background: T.bgAccentFaint, borderRadius: 3, border: `1px solid ${T.borderAccentFaint}` }}>
            <div style={{ fontSize: 11, color: T.textMuted }}>
              Shared via organization: <span style={{ color: T.borderAccent, fontWeight: 600 }}>{currentOrgName}</span>
            </div>
          </div>
        )}

        {/* Add share form (owner only) */}
        {isOwner && (
          <div style={{ marginBottom: 20 }}>
            <div style={{ fontSize: 10, color: T.textWeak, letterSpacing: ".1em", textTransform: "uppercase", marginBottom: 6 }}>Share with User</div>
            <div style={{ display: "flex", gap: 8 }}>
              <input
                value={email}
                onChange={e => { setEmail(e.target.value); setError(""); setSuccess(""); }}
                onKeyDown={e => { if (e.key === "Enter") handleShare(); }}
                placeholder="Email address"
                style={{ flex: 1, background: T.bgInput, border: `1px solid ${T.borderSecondary}`, borderRadius: 3, color: T.textSecondary, padding: "8px 12px", fontSize: 12, fontFamily: FONT, outline: "none" }}
              />
              <select value={role} onChange={e => setRole(e.target.value)} style={selectStyle}>
                <option value="viewer">Viewer</option>
                <option value="editor">Editor</option>
                <option value="admin">Admin</option>
              </select>
              <button onClick={handleShare} disabled={sharing || !email.trim()}
                style={{ background: T.borderAccent, border: "none", color: T.textOnGold, cursor: sharing ? "wait" : "pointer", padding: "8px 16px", borderRadius: 3, fontSize: 12, fontFamily: FONT, fontWeight: 700, opacity: (!email.trim() || sharing) ? 0.5 : 1, whiteSpace: "nowrap" }}>
                {sharing ? "..." : "Share"}
              </button>
            </div>
            {error && <div style={{ fontSize: 11, color: T.dangerText, marginTop: 6 }}>{error}</div>}
            {success && <div style={{ fontSize: 11, color: T.positive, marginTop: 6 }}>{success}</div>}
          </div>
        )}

        {/* Current shares */}
        <div>
          <div style={{ fontSize: 10, color: T.textWeak, letterSpacing: ".1em", textTransform: "uppercase", marginBottom: 8 }}>People with Access</div>
          {loading ? (
            <div style={{ fontSize: 11, color: T.textMuted, padding: "12px 0" }}>Loading...</div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
              {/* Owner row */}
              <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 12px", borderRadius: 3, background: T.bgAccentFaint }}>
                <div style={{ width: 28, height: 28, borderRadius: "50%", background: T.borderAccent, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 11, fontWeight: 700, color: T.textOnGold, flexShrink: 0 }}>
                  {(ownerName || "?").charAt(0).toUpperCase()}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 12, fontWeight: 600, color: T.textSubLabel }}>{ownerName || "Unknown"}</div>
                </div>
                <div style={{ fontSize: 10, color: T.borderAccent, fontWeight: 600, textTransform: "uppercase", letterSpacing: ".05em" }}>Owner</div>
              </div>

              {/* Shared users */}
              {shares.map(s => (
                <div key={s.userId} style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 12px", borderRadius: 3 }}
                  onMouseEnter={e => (e.currentTarget.style.background = T.bgHover)}
                  onMouseLeave={e => (e.currentTarget.style.background = "transparent")}>
                  <div style={{ width: 28, height: 28, borderRadius: "50%", background: T.bgAccentLight, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 11, fontWeight: 600, color: T.textTertiary, flexShrink: 0 }}>
                    {(s.displayName || s.email || "?").charAt(0).toUpperCase()}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 12, fontWeight: 500, color: T.textSubLabel }}>{s.displayName || s.email}</div>
                    {s.displayName && <div style={{ fontSize: 10, color: T.textPlaceholder }}>{s.email}</div>}
                  </div>
                  <div style={{ fontSize: 10, color: T.textMuted, textTransform: "uppercase", letterSpacing: ".05em" }}>{s.role}</div>
                  {isOwner && (
                    <button onClick={() => handleUnshare(s.userId)}
                      style={{ background: "none", border: "none", color: T.textWeak, cursor: "pointer", padding: "2px 6px" }}
                      title="Remove access">
                      <Icon.X size={12}/>
                    </button>
                  )}
                </div>
              ))}

              {shares.length === 0 && (
                <div style={{ fontSize: 11, color: T.textPlaceholder, padding: "8px 12px" }}>
                  No individual shares yet.{currentOrgId ? " Organization members have access via the org." : ""}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
